// The timeframe a performance figure is measured over.
//
// Two different things are available over two different sets of windows, and
// conflating them is the trap here:
//
//   * CHANGE IN VALUE can be computed for any window, because every account
//     carries a daily NAV series.
//   * The DECOMPOSITION — mark to market, realised, dividends and interest,
//     fees and commissions, net deposits — cannot. IBKR reports it for the
//     statement period, month to date and year to date, and for nothing else.
//     There is no "last 90 days" anywhere in a Flex statement.
//
// So the windows below are offered for the first, and only three of them can
// answer the second. The ones that cannot show dashes and say why, rather than
// deriving a plausible-looking number from a NAV series that cannot separate a
// deposit from a gain.
//
// `mtd` sits where a rolling "1M" would. That is deliberate: month to date is
// the window IBKR decomposes, a rolling thirty days is not, and labelling a
// month-to-date breakdown "1M" would put the wrong dates against the right
// numbers.

export const TIMEFRAMES = [
    { key: '1d', labelKey: 'timeframe.1d', days: 1 },
    { key: '1w', labelKey: 'timeframe.1w', days: 7 },
    { key: 'mtd', labelKey: 'timeframe.mtd', window: 'monthToDate' },
    { key: '3m', labelKey: 'timeframe.3m', months: 3 },
    { key: '6m', labelKey: 'timeframe.6m', months: 6 },
    { key: 'ytd', labelKey: 'timeframe.ytd', window: 'yearToDate' },
    { key: '1y', labelKey: 'timeframe.1y', months: 12 },
    { key: 'all', labelKey: 'timeframe.all', window: 'period' },
];

export const DEFAULT_TIMEFRAME = 'all';

const timeframeFor = (key) => TIMEFRAMES.find((f) => f.key === key) ?? TIMEFRAMES.at(-1);

/**
 * Where a window opens, given the series' last date.
 *
 * Two shapes, and they take their baseline from different places:
 *
 *   - A TO-DATE window (month, year) opens on a calendar boundary, and its
 *     baseline is the last close BEFORE that boundary — 31 August for a
 *     month-to-date figure. Starting from 1 September instead would silently
 *     drop whatever the first day of the month did.
 *   - A ROLLING window (a day, a week, three months) has no boundary of its
 *     own; the baseline is the close ON or just before the cutoff. "1D" from
 *     yesterday's close is the figure everyone means by it, and the strict rule
 *     above would reach past yesterday to the close before that.
 *
 * Measured off the newest point rather than off today, so a sync that is a few
 * days behind still shows a full window instead of a truncated one.
 */
function windowStart(frame, lastDate) {
    if (frame.window === 'period') return null;
    if (frame.window === 'monthToDate') {
        return { cutoff: `${lastDate.slice(0, 7)}-01`, inclusive: false };
    }
    if (frame.window === 'yearToDate') {
        return { cutoff: `${lastDate.slice(0, 4)}-01-01`, inclusive: false };
    }
    const at = new Date(`${lastDate}T00:00:00Z`);
    if (frame.days) {
        at.setUTCDate(at.getUTCDate() - frame.days);
    } else {
        at.setUTCMonth(at.getUTCMonth() - frame.months);
    }
    return { cutoff: at.toISOString().slice(0, 10), inclusive: true };
}

/** The last observation that sets the window's baseline, or null when the
 *  series does not reach back to it. */
function baselineFor(series, start) {
    if (start === null) return series[0] ?? null;
    const upTo = start.inclusive
        ? series.filter((p) => p.date <= start.cutoff)
        : series.filter((p) => p.date < start.cutoff);
    return upTo.at(-1) ?? null;
}

/**
 * Cumulative return series re-based to open the window at 0%, for the
 * comparison chart. Each series is compounded from its own baseline — the
 * last point at or before the cutoff — so a series is never measured from a
 * different day than the others.
 */
export function rebaseToWindow(points, key) {
    const series = (points || []).filter((p) => p && p.date && Number.isFinite(p.pct));
    if (series.length < 2) return series;
    return rebaseFrom(series, windowStart(timeframeFor(key), series.at(-1).date));
}

/** Same as rebaseToWindow, with the window measured off a given last date, so
 *  several series share one cutoff. */
export function rebaseAllToWindow(seriesList, key) {
    const last = seriesList.flatMap((s) => s.points.map((p) => p.date)).sort().at(-1);
    if (!last) return seriesList;
    const start = windowStart(timeframeFor(key), last);
    return seriesList.map((s) => ({ ...s, points: rebaseFrom(s.points, start) }));
}

function rebaseFrom(series, start) {
    if (!start || series.length < 2) return series;
    const base = baselineFor(series, start) ?? series[0];
    const growth = 1 + base.pct / 100;
    const kept = series.filter((p) => p.date >= base.date);
    if (kept.length < 2 || growth <= 0) return series;
    return kept.map((p) => ({ date: p.date, pct: ((1 + p.pct / 100) / growth - 1) * 100 }));
}

/** The series clipped to the window, for the chart. Never shorter than two
 *  points — a single point cannot be drawn, and an empty chart reads as a
 *  failure rather than as a quiet week. */
export function windowSeries(navHistory, key) {
    const series = (navHistory || []).filter((p) => p && p.date && p.total !== null);
    if (series.length < 2) return series;
    const start = windowStart(timeframeFor(key), series.at(-1).date);
    if (!start) return series;
    const clipped = series.filter((p) => p.date >= start.cutoff);
    return clipped.length >= 2 ? clipped : series.slice(-2);
}

/**
 * Change in value over the window, from the NAV series alone.
 *
 * `startingValue` is the last point BEFORE the window opened where there is
 * one, not the window's own first day: measuring from inside the window
 * silently drops whatever happened on its first day.
 *
 * Says nothing about how much of the change was earned. A deposit moves this
 * exactly as a gain does — which is the whole reason the decomposition below
 * exists and why this figure is never labelled P/L.
 */
export function valueChange(navHistory, key) {
    const series = (navHistory || []).filter((p) => p && p.date && p.total !== null);
    if (series.length === 0) return null;

    const last = series.at(-1);
    const start = windowStart(timeframeFor(key), last.date);
    // A series that does not reach the baseline begins inside the window, so
    // its own first day is the right starting point.
    const first = baselineFor(series, start)
        ?? series.find((p) => p.date >= (start?.cutoff ?? ''))
        ?? series[0];
    if (!first || first === last) return null;

    const from = Number(first.total);
    const to = Number(last.total);
    return {
        fromDate: first.date,
        toDate: last.date,
        startingValue: from,
        endingValue: to,
        change: to - from,
        // Suppressed when the baseline is a rounding error against the closing
        // value. The index account opens at $100 on the day it was created, so
        // every window reaching back that far divides by it and reports
        // "288,580%" — arithmetically exact and worthless. Under 1% of the
        // closing value means the account effectively started from nothing
        // inside this window, and a percentage of nothing says nothing.
        changePct: from > 0 && from >= Math.abs(to) / 100
            ? ((to - from) / from) * 100
            : null,
    };
}

/**
 * The decomposition for this window, or null when the broker does not report
 * one for it.
 *
 * Null is the point of this function. Returning a zeroed object would render as
 * a full breakdown that happens to say nothing happened.
 */
export function windowPerformance(performanceWindows, key) {
    const frame = timeframeFor(key);
    if (!frame.window || !performanceWindows) return null;
    return performanceWindows[frame.window] ?? null;
}

/**
 * What the named components leave unexplained.
 *
 * For the statement period the parts reconcile to `netPnl` exactly, because
 * both come from the same ChangeInNAV section. For month and year to date they
 * do not: the components are cash-basis figures from the cash report, while
 * `netPnl` is derived from NAV, which also carries accrued dividends and
 * interest and FX translation. On the live account that gap is around $1,300
 * over a year — small against the total and glaring in a column of numbers
 * that does not add up.
 *
 * Returned so the page can show it as a row rather than leave the reader to
 * find the discrepancy themselves. Null when the parts do reconcile.
 */
export function unexplained(performance) {
    if (!performance) return null;
    const parts = ['markToMarket', 'realized', 'dividendsAndInterest', 'feesAndCommissions']
        .map((key) => performance[key]);
    if (performance.netPnl === null || parts.some((v) => v === null || v === undefined)) return null;
    const residual = Number(performance.netPnl) - parts.reduce((a, v) => a + Number(v), 0);
    // A cent either way is rounding, not a missing component.
    return Math.abs(residual) < 0.5 ? null : residual.toFixed(2);
}

/**
 * The capital a window's P/L is measured against, or null when there is none
 * worth dividing by.
 *
 * Simple Dietz: the opening balance plus HALF the net deposits, which assumes
 * the money arrived mid-window. Neither obvious alternative survives this book.
 * Dividing by the opening balance alone credits the return to capital that was
 * not there yet — the index account opens at $100 and was funded to $288k, so
 * its P/L "as a percentage of the start" is five figures. Dividing by opening
 * plus ALL deposits treats money that landed yesterday as if it had been
 * working all year. The Flex statement gives totals, not the dates of the
 * flows, so the midpoint is the honest guess.
 *
 * Every row of the breakdown divides by the same basis, so the component
 * percentages add up to the net P/L percentage exactly as the amounts do.
 *
 * Not a time-weighted return, and never labelled as one. Where the broker
 * supplies a TWR the page shows that for the headline and uses this only to
 * size the parts.
 */
export function returnBasis(performance) {
    if (!performance) return null;
    // A breakdown that names its own capital — the private book, measured on
    // every cheque written — is measured on that, not on the midpoint guess.
    if (performance.capital !== null && performance.capital !== undefined) {
        const capital = Number(performance.capital);
        return capital > 0 ? capital : null;
    }
    const start = Number(performance.startingValue);
    const deposits = Number(performance.netDeposits ?? 0);
    const end = Number(performance.endingValue);
    if (performance.startingValue === null || performance.startingValue === undefined
        || !Number.isFinite(start) || !Number.isFinite(deposits)) return null;
    const basis = start + deposits / 2;
    // Same floor as `valueChange`: a basis under 1% of the closing value is an
    // account that effectively started from nothing, and a percentage of
    // nothing says nothing.
    if (!(basis > 0)) return null;
    if (Number.isFinite(end) && basis < Math.abs(end) / 100) return null;
    return basis;
}

/** `value` as a percentage of `basis`, or null when either is missing. */
export function pctOf(value, basis) {
    if (basis === null || basis === undefined) return null;
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? (n / basis) * 100 : null;
}

/** A signed percentage: "+1.23%", "−0.40%". The minus is U+2212, matching the
 *  signed amounts it sits beside. */
export function formatPct(pct, decimals = 2) {
    if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
    const body = Math.abs(pct).toLocaleString(undefined, {
        minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });
    // Rounds to zero: no sign, rather than "+0.00%" or "−0.00%".
    if (Math.round(Math.abs(pct) * 10 ** decimals) === 0) return `${body}%`;
    return `${pct < 0 ? '−' : '+'}${body}%`;
}
